import React, { useState } from "react";

import useDeleteAccount from "@fartherfinance/frontend/api/Accounts/hooks/useDeleteAccount";
import { ClientId, FartherAccountId } from "@fartherfinance/frontend/api/Types";

import LoadingModal from "../Shared/LoadingModal";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import SimpleModalWithCenteredButtons from "@src/sharedComponents/Modal/SimpleModalWithCenteredButtons/SimpleModalWithCenteredButtons";
import { handleApiError } from "@src/utils/handleApiError";

interface DeleteFartherManagedAccountModalProps {
  isOpen?: boolean;
  clientId: ClientId;
  accountId: FartherAccountId;
  accountDisplayName: string;
  clientDisplayName: string;
  onClose: () => void;
}

const DeleteFartherManagedAccountModal: React.FC<
  DeleteFartherManagedAccountModalProps
> = ({
  isOpen = true,
  accountId,
  accountDisplayName,
  clientId,
  clientDisplayName,
  onClose,
}) => {
  const [isMutating, setIsMutating] = useState<boolean>(false);
  const statusNotification = useStatusNotification();

  const auth = useAdvisorRequestAuth();
  const hardDeleteAccount = useDeleteAccount(auth);

  const onDelete = async () => {
    if (!accountId) {
      statusNotification("Error finding the account", "Error");
      return;
    }

    try {
      setIsMutating(true);

      await hardDeleteAccount({
        clientId: clientId,
        accountId: accountId,
        deleteAccountAction: "Hard",
      });

      setIsMutating(false);

      statusNotification("Account deleted", "Success");

      onClose();
    } catch (error) {
      handleApiError({
        error: error,
        errorMessage: "Error deleting account:",
        genericErrorMessage: "Error deleting account",
        notify: statusNotification,
      });
      setIsMutating(false);
    }
  };

  if (isMutating || accountDisplayName === undefined) {
    return (
      <LoadingModal
        title={"Delete Account"}
        text="This action will permanently delete the account and all records pertaining to it. To preserve the account's records archive it instead of deleting."
        onClose={onClose}
      />
    );
  }

  return (
    <SimpleModalWithCenteredButtons
      title="Delete Account"
      text={`This action will permanently delete "${accountDisplayName}" for ${clientDisplayName}. All records pertaining to this account will be permanently deleted. To preserve the account's records, archive it instead of deleting.`}
      secondaryButton={{
        text: "Cancel",
        onClick: onClose,
      }}
      primaryButton={{
        text: "Delete",
        onClick: onDelete,
      }}
      onClose={onClose}
      isOpen={isOpen}
    />
  );
};

export default DeleteFartherManagedAccountModal;
